import { useQuery } from "@tanstack/react-query";
import { Loader2, ShieldCheck, AlertTriangle } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import * as devicesApi from "@/services/devicesApi";
import { useUserTimezone } from "@/hooks/useUserTimezone";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";

interface DeviceCertificatesProps {
  deviceId: string;
}

const DeviceCertificates = ({ deviceId }: DeviceCertificatesProps) => {
  const { formatDate: formatTzDate } = useUserTimezone();
  const isMobile = useIsMobile();
  const { data: certificates, isLoading, error } = useQuery({
    queryKey: ['device-certificates', deviceId],
    queryFn: () => devicesApi.getDeviceCerts(deviceId),
    enabled: !!deviceId,
  });

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12 text-destructive">
        Failed to load certificates.
      </div>
    );
  }

  const certs = Array.isArray(certificates) ? certificates : [];

  if (certs.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <ShieldCheck className="h-10 w-10 text-muted-foreground mb-3" />
        <p className="text-muted-foreground">No certificates found on this device.</p>
      </div>
    );
  }

  const isExpired = (notAfter?: string) => {
    if (!notAfter) return false;
    return new Date(notAfter) < new Date();
  };

  const isExpiringSoon = (notAfter?: string) => {
    if (!notAfter) return false;
    const expiry = new Date(notAfter);
    const now = new Date();
    const daysUntil = (expiry.getTime() - now.getTime()) / (1000 * 60 * 60 * 24);
    return daysUntil > 0 && daysUntil <= 30;
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '—';
    return formatTzDate(dateStr);
  };

  const renderStatusBadge = (expired: boolean, expiringSoon: boolean) =>
    expired ? (
      <Badge variant="destructive" className="gap-1 text-xs">
        <AlertTriangle className="h-3 w-3" /> Expired
      </Badge>
    ) : expiringSoon ? (
      <Badge variant="outline" className="text-orange-600 border-orange-300 gap-1 text-xs">
        <AlertTriangle className="h-3 w-3" /> Expiring Soon
      </Badge>
    ) : (
      <Badge variant="outline" className="text-success border-success/40 gap-1 text-xs">
        <ShieldCheck className="h-3 w-3" /> Valid
      </Badge>
    );

  return (
    <div className={cn(isMobile ? "space-y-3" : "space-y-4")}>
      <div className="flex items-center justify-between">
        <h3 className={cn("font-semibold", isMobile ? "text-base" : "text-lg")}>
          Installed Certificates
        </h3>
        <span className="text-xs sm:text-sm text-muted-foreground">
          {certs.length} certificate{certs.length !== 1 ? 's' : ''}
        </span>
      </div>

      {isMobile ? (
        <div className="space-y-2">
          {certs.map((cert: any, index: number) => {
            const expired = isExpired(cert.notAfter);
            const expiringSoon = isExpiringSoon(cert.notAfter);
            return (
              <div key={index} className="border rounded-lg p-3 bg-card/50 min-h-[44px]">
                <div className="flex items-start justify-between gap-2 mb-1">
                  <p className="font-medium text-sm truncate flex-1 min-w-0">
                    {cert.commonName || '—'}
                  </p>
                  {renderStatusBadge(expired, expiringSoon)}
                </div>
                <p className="text-xs text-muted-foreground truncate mb-1">
                  {cert.issuer || '—'}
                </p>
                <div className="flex items-center justify-between gap-2 text-xs">
                  <span className={cn(
                    expired ? 'text-destructive font-medium'
                      : expiringSoon ? 'text-orange-600 font-medium'
                      : 'text-muted-foreground'
                  )}>
                    Expires {formatDate(cert.notAfter)}
                  </span>
                  {cert.isIdentity ? (
                    <Badge variant="outline" className="text-xs">Identity</Badge>
                  ) : (
                    <Badge variant="secondary" className="text-xs">Cert</Badge>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/50">
                <th className="text-left p-3 font-medium">Common Name</th>
                <th className="text-left p-3 font-medium">Issuer</th>
                <th className="text-left p-3 font-medium">Valid From</th>
                <th className="text-left p-3 font-medium">Expires</th>
                <th className="text-left p-3 font-medium">Type</th>
                <th className="text-left p-3 font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {certs.map((cert: any, index: number) => {
                const expired = isExpired(cert.notAfter);
                const expiringSoon = isExpiringSoon(cert.notAfter);

                return (
                  <tr key={index} className="border-b last:border-0 hover:bg-muted/30 transition-colors">
                    <td className="p-3 font-medium">{cert.commonName || '—'}</td>
                    <td className="p-3 text-muted-foreground">{cert.issuer || '—'}</td>
                    <td className="p-3 text-muted-foreground">{formatDate(cert.notBefore)}</td>
                    <td className="p-3">
                      <span className={expired ? 'text-destructive font-medium' : expiringSoon ? 'text-orange-600 font-medium' : 'text-muted-foreground'}>
                        {formatDate(cert.notAfter)}
                      </span>
                    </td>
                    <td className="p-3">
                      {cert.isIdentity ? (
                        <Badge variant="outline" className="text-xs">Identity</Badge>
                      ) : (
                        <Badge variant="secondary" className="text-xs">Certificate</Badge>
                      )}
                    </td>
                    <td className="p-3">{renderStatusBadge(expired, expiringSoon)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default DeviceCertificates;
